import { useEffect } from 'react'
import { Users, Plus, Minus, X, AlertCircle } from 'lucide-react'

export interface GuestCounts {
  adults: number
  children: number
  infants: number
  pets: number
}

type CounterKey = keyof GuestCounts

interface GuestsModalProps {
  isOpen: boolean
  counts: GuestCounts
  onChange: (counts: GuestCounts) => void
  onClose: () => void
  /** Which counters to show. Default = all four. Pass ['adults'] for room booking. */
  show?: CounterKey[]
  /** Optional cap: total guests <= maxGuests (when set). */
  maxGuests?: number
  title?: string
  subtitle?: string
}

const META: Record<CounterKey, { label: string; sub: string; min: number }> = {
  adults: { label: 'Adults', sub: 'Ages 13 or above', min: 1 },
  children: { label: 'Children', sub: 'Ages 2–12', min: 0 },
  infants: { label: 'Infants', sub: 'Under 2', min: 0 },
  pets: { label: 'Pets', sub: 'Service animals', min: 0 }
}

const Counter = ({
  label,
  sub,
  value,
  onChange,
  min,
  disabledInc
}: {
  label: string
  sub: string
  value: number
  onChange: (v: number) => void
  min: number
  disabledInc?: boolean
}) => (
  <div className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
    <div>
      <div className="font-semibold text-gray-900 text-sm">{label}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onChange(Math.max(min, value - 1))
        }}
        className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:border-primary-500 hover:bg-primary-50 transition-colors active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={value <= min}
        aria-label={`Decrease ${label}`}
      >
        <Minus className="h-4 w-4 text-gray-700" />
      </button>
      <span className="w-7 text-center font-semibold text-base tabular-nums">{value}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onChange(value + 1)
        }}
        disabled={disabledInc}
        className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:border-primary-500 hover:bg-primary-50 transition-colors active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label={`Increase ${label}`}
      >
        <Plus className="h-4 w-4 text-gray-700" />
      </button>
    </div>
  </div>
)

const GuestsModal = ({
  isOpen,
  counts,
  onChange,
  onClose,
  show = ['adults', 'children', 'infants', 'pets'],
  maxGuests,
  title = "Who's coming?",
  subtitle = 'Add the number of travellers'
}: GuestsModalProps) => {
  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const total = (counts.adults || 0) + (counts.children || 0) + (counts.infants || 0)
  const limitReached = !!maxGuests && total >= maxGuests

  const update = (key: CounterKey, value: number) => {
    // ensure adults stays >= 1 when anyone else is present
    let next: GuestCounts = { ...counts, [key]: value }
    if (key !== 'adults' && next.children + next.infants + next.pets > 0) {
      next.adults = Math.max(1, next.adults)
    }
    if (maxGuests && key !== 'pets') {
      // enforce max guests across counted categories
      const counted = ['adults', 'children', 'infants'] as CounterKey[]
      const nextTotal = counted.reduce((sum, k) => sum + (next[k] || 0), 0)
      if (nextTotal > maxGuests) return
    }
    onChange(next)
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-8">
        <div
          className="bg-white w-full h-full md:h-auto md:max-w-md md:max-h-[85vh] md:rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>
                <p className="text-xs md:text-sm text-gray-600">{subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/60 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Counters */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="divide-y divide-gray-100">
              {show.map((key) => (
                <Counter
                  key={key}
                  label={META[key].label}
                  sub={META[key].sub}
                  value={counts[key] || 0}
                  min={META[key].min}
                  disabledInc={key !== 'pets' && limitReached}
                  onChange={(v) => update(key, v)}
                />
              ))}
            </div>

            {maxGuests && (
              <div
                className={`mt-4 flex items-center gap-2 text-xs rounded-xl p-3 ${
                  limitReached
                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                    : 'bg-gray-50 text-gray-600'
                }`}
              >
                {limitReached ? <AlertCircle className="h-4 w-4" /> : <Users className="h-4 w-4 text-primary-500" />}
                <span>
                  {limitReached
                    ? `Maximum ${maxGuests} guests for this room.`
                    : `Up to ${maxGuests} guests allowed for this room.`}
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50 flex items-center justify-between">
            <button
              onClick={() =>
                onChange({ adults: META.adults.min, children: 0, infants: 0, pets: 0 })
              }
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default GuestsModal
